import React, { useEffect, useState } from 'react';

import Image from 'next/image';
import bubbleVariants from '@components/Features/TransientThoughtBubbleVariants';
import cloudVariants from '@components/Features/TransientThoughtCloudVariants';
import { WordCloudStylePicker } from '@components/Presentation/main/Settings/units/WordCloudStylePreview';

import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@atoms/new/dropdown';
import SelectableRadioGroup from '@atoms/new/SelectableRadioGroup';

import SettingSwitch from '@molecules/SettingSwitch';
import SettingBlock from '@components/Presentation/main/Settings/units/SettingBlock';
import RatingPollsOptions from './RatingPollsOptions';

import {
  TOOLTIP_CONTENT_MAP,
  SHOW_USERNAME_TOOLTIP_CONTENT_MAP,
  linkCategories,
  getImage,
  convertToPascalCase,
  playbackOptions,
  mapContinents,
  getOptionName,
  fantasticFansWinnerOptions
} from '@services/interactions.service';
import { interactionsName } from '@services/utils.service';
import { ALL_MCQ_SCORERS_ID } from '@components/AdhocInteractionPopup';

import SoundSettings from '@components/Presentation/main/Settings/units/SoundSettings';
import Button from '@atoms/new/button';

import { useStreamData } from '@lib/hooks/useStreamData';
import Input from '@atoms/Input';
import ChoiceCircleOptions from './ChoiceCircleOptions';
import { CaretDownIcon, CheckIcon, CheckIcon as DoneRounded } from '@phosphor-icons/react';
import { wheelSoundEnabledChecker } from '@components/Features/WinnerWheel/helpers/wheelSoundEnabledChecker';
import { Slider } from '@heroui/react';
const InteractionSetting = ({
  featureType,
  setting,
  updateSetting,
  handleSavePresentation,
  options,
  isInstantCustomisationPopup = false,
  updateBulkOptions,
  deleteOption,
  currentRosFeature,
  setOptions
}) => {
  const showAudienceResponses = () => !['wheel', 'newWheel', 'multipleChoice', 'linkLibrary', 'fantasticFans', 'leaderboard', 'quiz', 'credits'].includes(featureType);
  const showResultLayout = () => ['freeForm', 'pollWithOptions', 'ratingPolls', 'multipleChoice', 'textTrack', 'quiz'].includes(featureType);
  const showLanguage = () => ['freeForm', 'pollWithOptions', 'wordCloud', 'talkingTiles', 'multipleChoice', 'textTrack'].includes(featureType);

  const tooltipContent = TOOLTIP_CONTENT_MAP[featureType] || 'StreamAlive will count multiple responses per participant.';
  const showUserNameTooltipContent = SHOW_USERNAME_TOOLTIP_CONTENT_MAP[featureType] || 'Each title will show the username of the commenter.';

  const { selectedLocation } = useStreamData(s => ({ selectedLocation: s.selectedLocation }));

  const hasOneAnswerPerPerson = () => ([
    'map',
    'freeForm',
    'pollWithOptions',
    'wordCloud',
    'emojisEverywhere',
    'transientThought',
    'talkingTiles'
  ].includes(featureType));

  const hasCombineWords = () => ([
    'freeForm',
    'wordCloud'
  ].includes(featureType));

  const hasSplitPhrasesIntoWords = () => ([
    'wordCloud',
    'textTrack'
  ].includes(featureType));

  const hasCommentsOverlay = () => ([
    'map',
    'freeForm',
    'pollWithOptions',
    'wordCloud',
    'emojisEverywhere',
    'ratingPolls'
  ].includes(featureType));

  const hasShowUserName = () => ([
    'transientThought',
    'talkingTiles',
    'Quick Questions'
  ].includes(featureType));

  const hasDisplayStyle = () => ([
    'freeForm',
    'pollWithOptions',
    'ratingPolls',
    'multipleChoice'].includes(featureType));

  // wordCloud rotation is now controlled by the Display Style picker below;
  // only textTrack (no style picker) still uses this toggle.
  const hasShowSomeWordsVertically = () => ([
    'textTrack'
  ].includes(featureType));

  const hasShowProfilePictures = () => ([
    'freeForm',
    'pollWithOptions',
    'multipleChoice',
    'ratingPolls'
  ].includes(featureType));

  const hasConfetti = () => (['wheel', 'newWheel'].includes(featureType));

  const hasDefaultView = () => (['map'].includes(featureType));
  const hasShowLatestCommentsFirst = () => (['transientThought'].includes(featureType));
  const hasOptionsList = () => (['pollWithOptions'].includes(featureType));
  const hasShowResponses = () => (['multipleChoice', 'quiz'].includes(featureType));
  const hasPlaybackSpeed = () => (['transientThought'].includes(featureType));

  const multipleChoiceFilterOptions = ['All', 10, 20];

  const splitWordsKey = featureType === 'textTrack' ? 'textTrackSplitWords' : 'SplitWords';
  const usernameKey = featureType === 'transientThought' ? 'transientThoughtShowUsername' : 'showUserName';

  const profilePicturesKeyMap = {
    freeForm: 'pollShowProfilePictures',
    pollWithOptions: 'pollWithOptionsShowProfilePictures',
    multipleChoice: 'multipleChoiceShowProfilePictures',
    ratingPolls: 'ratingPollShowProfilePictures'
  };
  const profilePicturesKey = profilePicturesKeyMap[featureType];
  const [wheelOptions, setWheelOptions] = useState([]);

  const getInteractionName = (interaction) => {
    if (interaction?.type === 'multipleChoice' && interaction?.id === ALL_MCQ_SCORERS_ID) return 'Your top scorers';
    return interactionsName[interaction?.type]?.name || 'All Interactions';
  };

  useEffect(() => {
    if (!['wheel', 'newWheel'].includes(featureType)) return;
    wheelSoundEnabledChecker({
      isWheelSpinSoundEnabled: setting.isWheelSpinSoundEnabled,
      isWheelButtonSoundEnabled: setting.isWheelButtonSoundEnabled,
      isWheelResultSoundEnabled: setting.isWheelResultSoundEnabled,
      isenableWheelSound: setting.enableWheelSound,
      updateSetting
    });
  }, [featureType, setting.enableWheelSound, setting.isWheelSpinSoundEnabled, setting.isWheelButtonSoundEnabled, setting.isWheelResultSoundEnabled]);

  useEffect(() => {
    if (wheelOptions.length === 0) {
      setWheelOptions(currentRosFeature?.options || []);
    }
    // if (options.length === 0) {
    //   setWheelOptions(options);
    // }
  }, [currentRosFeature?.options]);

  const handleWheelOptionsChange = (options) => {
    setWheelOptions(options);
    setOptions(options);
  };

  useEffect(() => {
    if (!setting?.wheelInteractionType) {
      updateSetting('wheelInteractionType', 'All Interactions');
    }
    if (setting?.wheelShowUser === undefined) {
      updateSetting('wheelShowUser', '1');
    }
  }, [setting.wheelShowUser, setting.wheelInteractionType]);

  useEffect(() => {
    if (!setting?.ttPlaybackSpeed || setting?.ttPlaybackSpeed === undefined) { updateSetting('ttPlaybackSpeed', 'default'); }
  }, [setting]);

  const wheelTypes = {
    keyword: { label: 'Keyword', tag: 'automatic', value: 'keyword', description: 'Participants type a keyword to add their names', display: 'Keyword' },
    chat: { label: 'Participation', tag: 'automatic', value: 'chat', description: 'Get participants from chat', display: 'Participation' },
    customNames: { label: 'Custom names', tag: 'manual', value: 'customNames', description: 'Enter names to display on the spinner', display: 'Custom' }
  };

  const handleWheelType = (type) => {
    updateSetting('wheelSelectionType', type);
    if (type === 'keyword') {
      updateSetting('enableKeyword', true);
      updateSetting('wheelKeyword', setting?.wheelKeyword ?? 'spin');
    } else {
      updateSetting('enableKeyword', false);
    }
  };

  useEffect(() => {
    handleWheelType(setting?.wheelSelectionType);
  }, []);

  return <>
    {showAudienceResponses() && <SettingBlock
      title={featureType === 'quiz' ? '' : 'Audience responses'}
      hasBorderTop
      addPadding={false}
    >
      {hasOneAnswerPerPerson() && (
        <SettingSwitch
          label="Allow multiple responses"
          tooltipContent={tooltipContent}
          settingKey="oneUoneV"
          settingValue={!setting.oneUoneV}
          onChangeHandler={(key, value) => updateSetting(key, !value)}
        />
      )}

      {hasCombineWords() && (
        <SettingSwitch
          label={featureType === 'freeForm' ? 'Allow minor typos' : 'Combine similar answers'}
          tooltipContent={featureType === 'freeForm' ? 'Similar responses from the chat will be counted together.' : 'Similar responses will be grouped, with the added flexibility of accommodating minor typos.'}
          settingKey="CombineWords"
          settingValue={setting.CombineWords}
          onChangeHandler={updateSetting}
        />
      )}

      {hasSplitPhrasesIntoWords() && (
        <SettingSwitch
          label="Split phrases into words"
          tooltipContent="All responses will show up as individual words."
          settingKey={splitWordsKey}
          settingValue={setting?.[splitWordsKey]}
          onChangeHandler={updateSetting}
        />
      )}

      {hasCommentsOverlay() && (
        <SettingSwitch
          label="Show comments overlay"
          tooltipContent="Show audience responses as a callout in the lower right corner of the presentation."
          settingKey="CommentsOverlay"
          settingValue={setting.CommentsOverlay}
          onChangeHandler={updateSetting}
        />
      )}

      {hasShowUserName() && (
        <SettingSwitch
          label="Show username"
          tooltipContent={showUserNameTooltipContent}
          settingKey={usernameKey}
          settingValue={setting?.[usernameKey]}
          onChangeHandler={updateSetting}
        />
      )}

      {hasShowLatestCommentsFirst() && (
        <SettingSwitch
          label="Show recent comments first"
          tooltipContent="All responses will show up as individual words."
          settingKey="showLatestCommentsFirst"
          settingValue={setting.showLatestCommentsFirst}
          onChangeHandler={updateSetting}
        />
      )}
    </SettingBlock>}

    {hasDefaultView() && <SettingBlock
      title="Map view"
      tooltip="Initial map view to be shown. You can still pan and zoom over the entire map during a presentation."
      titleMargin="sm"
      hasBorderTop
      addPadding={false}
    >
      <Dropdown className="z-1 w-full">
        <DropdownTrigger>
          <Button
            kind="tertiary"
            fullWidth
            className="flex items-center gap-3"
          >
            <p className='font-normal'>{isInstantCustomisationPopup ? selectedLocation : getOptionName(setting)}</p>
            <div className="ml-auto">
              <CaretDownIcon size={16} />
            </div>
          </Button>
        </DropdownTrigger>
        <DropdownMenu>
          {mapContinents.map((option) => (
            <DropdownItem
              key={option.value}
              value={option.value}
              onAction={() => updateSetting('DefaultView', option.value)}
              endContent={
                option.value === (isInstantCustomisationPopup ? selectedLocation : setting.DefaultView) ? (
                  <DoneRounded size={20} />
                ) : null
              }
            >
              {option.name}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </SettingBlock>
    }

    <SoundSettings
      featureType={featureType}
      setting={setting}
      updateSetting={updateSetting}
      uiOptions={{
        showLabel: true,
        showBorder: true,
        showEnabledSwitch: true,
        checkBox: false,
        showPadding: !['map', 'talkingTiles'].includes(featureType)
      }}
    />

    {(featureType === 'ratingPolls') && <RatingPollsOptions
      setting={setting}
      updateSetting={updateSetting}
      updateBulkOptions={updateBulkOptions}
      deleteOption={deleteOption}
    />}

    {(featureType === 'newWheel') && <>
      <SettingBlock
        title='Get names from'
        hasBorderTop
        addPadding={false}
        defaultExpanded={true}
      >
        <Dropdown className="z-1 w-full max-w-[360px]">
          <DropdownTrigger>
            <Button
              kind="tertiary"
              fullWidth
            >
              <div className="flex py-2 w-[90%] gap-3">
                <div className="flex text-sm flex-initial flex-col line-clamp-1 font-normal">
                  <p className="text-left truncate">{wheelTypes[setting?.wheelSelectionType]?.display}</p>
                </div>
              </div>
              <div className="ml-auto">
                <CaretDownIcon size={16} />
              </div>
            </Button>
          </DropdownTrigger>
          <DropdownMenu>
            {Object.values(wheelTypes).map((data) => (
              <DropdownItem
                key={data.value}
                value={setting?.wheelSelectionType || 'chat'}
                onAction={() => handleWheelType(data.value)}
              >
                <div className="flex gap-3 items-center justify-between">
                  <div className="flex text-sm flex-initial flex-col line-clamp-1">
                    <p className="text-left text-default-900 flex items-center gap-2">{data.label} <span className="dark:text-default-100 bg-grey-200 rounded-md text-xs px-2 py-1">{data.tag}</span></p>
                    <p className="text-left text-grey-700 mt-1">{data.description}</p>
                  </div>
                  <div>
                    {setting?.wheelSelectionType === data.value && <CheckIcon size={20} className="text-default-900" />}
                  </div>
                </div>
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
        {setting.enableKeyword && (
          // <SettingBlock
          //   hasBorderTop={false}
          //   titleMargin="sm"
          //   addPadding={false}
          //   hasBorderDown={false}
          // >
          <Input
            placeholder="Keyword..."
            value={setting.wheelKeyword ?? ''}
            onChange={(e) => {
              updateSetting('wheelKeyword', e.target.value);
            }}
            size="lg"
            maxLength={20}
          />
          // </SettingBlock>
        )}
        {setting?.wheelSelectionType === 'customNames' && (
          <div className="mb-6 ">
            <p className='text-sm text-default-600'>Enter names to display on the spinner</p>
            <div className="w-full">
              <ChoiceCircleOptions
                options={options}
                setOptions={setOptions}
              />
            </div>
          </div>
        )}
      </SettingBlock>

      {/* {!setting.enableKeyword && (
        <SettingBlock
          title="Select an interaction"
          titleMargin="sm"
          hasBorderTop={false}
          hasBorderDown={false}
          addPadding={false}
          className="-mb-6"
          defaultExpanded
        >
          <Dropdown className="z-1 w-full max-w-[360px]">
            <DropdownTrigger>
              <Button
                kind="tertiary"
                fullWidth
                className="h-[64px] flex items-center gap-3"
              >
                <div className="flex gap-3 py-2 px-2 w-[90%]">
                  <div className="flex items-center justify-center text-default-900">
                    <SlideImage slideType={selectedInteraction?.type} color='#707070' />
                  </div>
                  <div className="flex text-sm flex-initial flex-col line-clamp-1">
                    <div className="text-left line-clamp-1">{getInteractionName(selectedInteraction)}
                      <span className="text-default-500"> {selectedInteraction?.totalCommenters || 0} {selectedInteraction?.totalCommenters <= 1 ? 'commenter' : 'commenters'}</span>
                    </div>
                    <p className="text-left line-clamp-1 font-normal text-sm text-default-700">{selectedInteraction?.title || 'No Title for this Interaction'}</p>
                  </div>
                </div>
                <div className="ml-auto">
                  <CaretDownIcon size={16} />
                </div>
              </Button>
            </DropdownTrigger>
            <DropdownMenu>
              {winnerWheelsData.map((data) => (
                <DropdownItem
                  key={data.id}
                  value={data}
                  onAction={() => {
                    setSelectedInteraction(data);
                    updateSetting("wheelInteractionId", data.id);
                    updateSetting("wheelInteractionType", data.type);
                    updateSetting("wheelInteractionTitle", data.title);
                  }}
                >
                  <Tooltip content={data?.title || 'No Title for this Interaction'} placement="right">
                    <div className="flex gap-3">
                      <div className="flex items-center justify-center text-grey-900">
                        <SlideImage slideType={data.type} color='#707070' />
                      </div>
                      <div className="flex text-sm flex-initial flex-col line-clamp-1">
                        <div className="text-left line-clamp-1">{getInteractionName(data)}
                          <span className="text-default-500"> {data?.totalCommenters || 0} {data?.totalCommenters <= 1 ? 'commenter' : 'commenters'}</span>
                        </div>
                        <p className="text-left line-clamp-1 text-default-700">{data?.title || 'No Title for this Interaction'}</p>
                      </div>
                      <div className="flex items-center justify-center text-grey-900">
                        {data?.id === selectedInteraction?.id ? (
                          <DoneRounded size={20} />
                        ) : <div className='w-5 h-5'></div>}
                      </div>
                    </div>
                  </Tooltip>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </SettingBlock>
      )
      }
      */}
    </>}

    {/* {(selectedInteraction?.type !== 'multipleChoice' && featureType === 'newWheel' && !setting.enableNames && !setting.enableKeyword) && <SettingBlock
      title="Filter users with at least..."
      tooltip="Choose the minimum number of comments a participant must have typed to be included."
      titleMargin="sm"
      hasBorderTop={false}
      hasBorderDown={true}
      addPadding={false}
    >
      <Dropdown className="z-1 w-full">
        <DropdownTrigger>
          <Button
            kind="tertiary"
            fullWidth
            className="flex items-center gap-3"
            isDisabled={setting.enableKeyword}
          >
            <p className='font-normal'>{setting.wheelShowUser} comment</p>
            <div className="ml-auto">
              <CaretDownIcon size={16} />
            </div>
          </Button>
        </DropdownTrigger>
        <DropdownMenu>
          {wheelShowUserOptions.map((option) => (
            <DropdownItem
              key={option}
              value={option}
              onAction={() => updateSetting('wheelShowUser', option)}
              endContent={
                option === setting.wheelShowUser ? (
                  <DoneRounded size={20} />
                ) : null
              }
            >
              {option} comment
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </SettingBlock>}

    {(selectedInteraction?.type === 'multipleChoice' && featureType === 'newWheel' && !setting.enableNames) && <SettingBlock
      title="Choose from the list of users..."
      titleMargin="sm"
      addPadding={false}
    >
      <Dropdown className="z-1 w-full">
        <DropdownTrigger>
          <Button
            kind="tertiary"
            fullWidth
            className="flex items-center gap-3"
          >
            <p className='font-normal'>
              {setting.wheelMcqLimit !== 'All' && 'Top'} {setting.wheelMcqLimit || 'All'} {setting.wheelMcqLimit === 'All' ? 'users' : 'highest scorers'}
            </p>
            <div className="ml-auto">
              <CaretDownIcon size={16} />
            </div>
          </Button>
        </DropdownTrigger>
        <DropdownMenu>
          {multipleChoiceFilterOptions.map((option) => (
            <DropdownItem
              key={option}
              value={option}
              onAction={() => updateSetting('wheelMcqLimit', option)}
              endContent={
                option === setting.wheelMcqLimit ? (
                  <DoneRounded size={20} />
                ) : null
              }
            >
              {option !== 'All' && 'Top'} {option} {option === 'All' ? 'users' : 'highest scorers'}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </SettingBlock>} */}

    {showResultLayout() && <SettingBlock
      title="Result Layout"
      hasBorderTop
      addPadding={false}
    >
      {hasDisplayStyle() && <SettingSwitch
        label="Show as percentages (%)"
        tooltipContent="When enabled, the percentage of votes is shown. Else, the number of votes are shown."
        settingKey="DisplayStyle"
        settingValue={setting.DisplayStyle === 'percentage'}
        onChangeHandler={updateSetting}
      />}

      {hasOptionsList() && (
        <SettingSwitch
          label="Show numerical options"
          tooltipContent="When enabled, options are labelled as 1, 2, 3. Else, as A, B, C."
          settingKey="OptionsList"
          settingValue={setting.OptionsList === 'number'}
          onChangeHandler={updateSetting}
        />
      )}

      {hasShowSomeWordsVertically() && (
        <SettingSwitch
          label="Show some words vertical"
          tooltipContent="Allow some words to be displayed vertically."
          settingKey="Vertical"
          settingValue={setting.Vertical}
          onChangeHandler={updateSetting}
        />
      )}

      {hasShowResponses() && (
        <SettingSwitch
          label="Show responses in real-time"
          tooltipContent="Responses will be reflected in the poll in real-time"
          settingKey="showResponses"
          settingValue={setting.showResponses}
          onChangeHandler={updateSetting}
        />
      )}

      {hasShowProfilePictures() && profilePicturesKey && (
        <SettingSwitch
          label="Display profile pictures"
          tooltipContent="Show profile pictures to help identify participants."
          settingKey={profilePicturesKey}
          settingValue={setting?.[profilePicturesKey]}
          onChangeHandler={updateSetting}
        />
      )}

    </SettingBlock>}

    {featureType === 'newWheel' && <SettingBlock
      title="Audience responses"
      hasBorderTop
      addPadding={false}
    >
      <SettingSwitch
        label="Show comments"
        tooltipContent="See what the winner typed in the chat during the interaction."
        settingKey="enableWheelComments"
        settingValue={setting?.enableWheelComments}
        onChangeHandler={updateSetting}
      />
    </SettingBlock>}

    {hasConfetti() && <>
      <SettingBlock
        title="Visual effects"
        hasBorderTop
        addPadding={false}
      >
        <SettingSwitch
          label="Enable confetti"
          tooltipContent="Confetti will rain down when a winner is selected."
          settingKey="enableConfetti"
          settingValue={setting?.enableConfetti}
          onChangeHandler={updateSetting}
        />
      </SettingBlock>

      {/* {featureType === 'newWheel' &&
        <SettingBlock
          title="Custom names"
          tooltip="Your custom names and chat names will both appear on the wheel."
          hasBorderTop
          hasBorderDown={false}
          isAccordion={true}
          addPadding={false}
        >
          <textarea
            className="w-full h-31 p-2 rounded-md border border-grey-400 dark:border-grey-700 dark:bg-transparent resize-none focus:outline-blue-500 whitespace-pre-line"
            placeholder={'Name 1\nName 2\nName 3'}
            value={wheelOptions.map((option) => option.body).join('\n')}
            onChange={(e) => handleWheelOptionsChange(e.target.value.split('\n').map((body) => ({ body })))}
          />
        </SettingBlock>} */}

      <SettingBlock
        title="Sound"
        hasBorderTop
        titleMargin="sm"
        addPadding={false}
      >
        <SettingSwitch
          label={featureType === 'wheel' ? 'Choice Circle Sound' : 'Winning Wheel Sound'}
          tooltipContent=""
          settingKey="enableWheelSound"
          settingValue={setting.enableWheelSound}
          onChangeHandler={() => {
            const nextValue = !setting.enableWheelSound;
            updateSetting('enableWheelSound', nextValue);
            if (!nextValue) {
              updateSetting('isWheelSpinSoundEnabled', false);
              updateSetting('isWheelButtonSoundEnabled', false);
              updateSetting('isWheelResultSoundEnabled', false);
            }
          }}
        />
        <SoundSettings featureType="wheelSpin" setting={setting} uiOptions={{
          checkBox: true,
          showLabel: false,
          showBorder: false,
          showEnabledSwitch: true,
          className: 'p-0! p-2!',
        }} onChangeHandler={(e) => {
          updateSetting(e.key, e.target.checked);
          if (e.target.checked && !setting.enableWheelSound) {
            updateSetting('enableWheelSound', true);
          }
        }} updateSetting={(key, value) => {
          updateSetting(key, value);
        }} isDisabled={!setting.enableWheelSound}
        />
        <SoundSettings featureType="wheelButton" setting={setting} uiOptions={{
          checkBox: true,
          showLabel: false,
          className: 'p-0! p-2!',
          showBorder: false,
          showEnabledSwitch: false,
        }} onChangeHandler={(e) => {
          updateSetting(e.key, e.target.checked);
          if (e.target.checked && !setting.enableWheelSound) {
            updateSetting('enableWheelSound', true);
          }
        }} updateSetting={(key, value) => {
          updateSetting(key, value);
        }} isDisabled={!setting.enableWheelSound}
        />
        <SoundSettings featureType="wheelResult" setting={setting} uiOptions={{
          checkBox: true,
          showLabel: false,
          showBorder: false,
          className: 'p-0! p-2!',
          showEnabledSwitch: false
        }} onChangeHandler={(e) => {
          updateSetting(e.key, e.target.checked);
          if (e.target.checked && !setting.enableWheelSound) {
            updateSetting('enableWheelSound', true);
          }
        }} updateSetting={(key, value) => {
          updateSetting(key, value);
        }} isDisabled={!setting.enableWheelSound}
        />
      </SettingBlock>
    </>}

    {(featureType === 'linkLibrary') && <SettingBlock
      title="Select platform"
      tooltip="All links are shown by default. Choose to filter by website if needed."
      titleMargin="sm"
      hasBorderTop
      addPadding={false}
    >
      <Dropdown className="z-1 w-full">
        <DropdownTrigger>
          <Button
            kind="tertiary"
            fullWidth
            className="flex items-center gap-3"
          >
            <div className="w-5 h-5">
              <Image
                src={getImage(setting).url}
                alt={getImage(setting).name}
                width={20}
                height={20}
              />
            </div>
            <p className='font-normal'>{convertToPascalCase(setting.LinkType)}</p>
            <div className="ml-auto">
              <CaretDownIcon size={16} />
            </div>
          </Button>
        </DropdownTrigger>
        <DropdownMenu>
          {Object.entries(linkCategories).map(([k, v]) => (
            <DropdownItem
              key={k}
              value={k}
              onAction={() => updateSetting('LinkType', k)}
              startContent={
                <Image
                  src={v.img}
                  alt={v.name}
                  width={20}
                  height={20}
                />
              }
              endContent={
                k === setting.LinkType ? (
                  <DoneRounded size={20} />
                ) : null
              }
            >
              {v.name}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </SettingBlock>
    }

    {featureType === 'transientThought' && (
      <SettingBlock title="Display Style" addPadding={false}>
        <div className="flex flex-col gap-2">
          {[
            { value: 'cloud', label: 'Thought Cloud' },
            { value: 'bubbles', label: 'Chat Bubble' },
          ].map(({ value, label }) => {
            const isSelected = (setting.transientThoughtDisplayStyle || 'cloud') === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => updateSetting('transientThoughtDisplayStyle', value)}
                className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${isSelected ? 'border-primary bg-primary-50 dark:bg-primary-900/20' : 'border-divider hover:border-default-400'}`}
              >
                <div className="flex-1 flex items-center justify-between">
                  <span className="text-sm">{label}</span>
                  <div className="w-16 h-8 overflow-hidden flex items-center justify-center opacity-80">
                    {value === 'cloud'
                      ? React.cloneElement(cloudVariants['small-tl']('#FABBFF'), { width: 60, height: 30 })
                      : React.cloneElement(bubbleVariants['small-tl']('#F26A60'), { width: 60, height: 24 })}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </SettingBlock>
    )}

    {['wordCloud', 'emojisEverywhere'].includes(featureType) && (
      <SettingBlock title="Display Style" addPadding={false}>
        <WordCloudStylePicker
          value={setting.wordCloudDisplayStyle || 'chips'}
          onChange={(value) => updateSetting('wordCloudDisplayStyle', value)}
          variant={featureType === 'emojisEverywhere' ? 'emoji' : 'word'}
        />
      </SettingBlock>
    )}

    {hasPlaybackSpeed() && <SettingBlock
      title="Playback Speed"
      tooltip="This controls the speed at which new comments are shown. Larger comments will be onscreen for longer."
      addPadding={false}
    >
      <Dropdown className="z-1 w-full">
        <DropdownTrigger>
          <Button
            kind="tertiary"
            fullWidth
            className="flex items-center gap-3"
          >
            <p className='font-normal'>{setting.ttPlaybackSpeed && playbackOptions[setting.ttPlaybackSpeed]}</p>
            <div className="ml-auto">
              <CaretDownIcon size={16} />
            </div>
          </Button>
        </DropdownTrigger>
        <DropdownMenu>
          {Object.keys(playbackOptions).map((key) => (
            <DropdownItem
              key={key}
              value={key}
              onAction={() => updateSetting('ttPlaybackSpeed', key)}
              endContent={
                key === setting.ttPlaybackSpeed ? (
                  <DoneRounded size={20} />
                ) : null
              }
            >
              {playbackOptions[key]}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </SettingBlock>}

    {showLanguage() && <SettingBlock
      title="Language"
      itemGap="sm"
      addPadding={false}
    >
      <SettingSwitch
        label="Filter out profanity"
        tooltipContent="Any comments containing explicit profanity (English) will not be shown."
        settingKey="ProfanityFilter"
        settingValue={setting.ProfanityFilter}
        onChangeHandler={updateSetting}
      />
      <p className="text-xs w-[80%] text-default-700">
        Help us improve our profanity filtering by emailing us at <a href="mailto:someone@example.com" className='text-blue-600'>hi@streamalive.com</a>
      </p>
    </SettingBlock>}

    {(featureType === 'fantasticFans') &&
      <SettingBlock
        itemGap="sm"
        addPadding={false}
      >
        <SelectableRadioGroup
          options={fantasticFansWinnerOptions}
          value={setting.fantasticFansShowWinners || 'all'}
          label='Show top'
          onChange={(value) => updateSetting('fantasticFansShowWinners', value)}
        />
      </SettingBlock>}

    {(featureType === 'leaderboard') &&
      <SettingBlock
        itemGap="sm"
        addPadding={false}
      >
        <SelectableRadioGroup
          options={fantasticFansWinnerOptions}
          value={setting.leaderboardShowWinners || 'all'}
          label='Show top'
          onChange={(value) => updateSetting('leaderboardShowWinners', value)}
        />
      </SettingBlock>}

    {(featureType === 'credits') && <>
      <SettingBlock title="Top commenters label" titleMargin="sm">
        <Input
          placeholder="Top Commenters"
          value={setting.creditsTopLabel ?? ''}
          onChange={(e) => updateSetting('creditsTopLabel', e.target.value)}
          size="lg"
          maxLength={80}
        />
      </SettingBlock>

      <SettingBlock title="Episode label" titleMargin="sm">
        <Input
          placeholder="Thank You"
          value={setting.creditsEpisodeLabel ?? ''}
          onChange={(e) => updateSetting('creditsEpisodeLabel', e.target.value)}
          size="lg"
          maxLength={120}
        />
      </SettingBlock>

      <SettingBlock title="Closing line" titleMargin="sm">
        <Input
          placeholder="e.g. See you next time!"
          value={setting.creditsClosingLine ?? ''}
          onChange={(e) => updateSetting('creditsClosingLine', e.target.value)}
          size="lg"
          maxLength={120}
        />
      </SettingBlock>

      <SettingBlock title="Scroll speed" addPadding>
        <div className="flex justify-between items-center text-sm mb-2">
          <span>Fast</span>
          <span>Medium</span>
          <span>Slow</span>
        </div>
        <Slider
          color="danger"
          step={10}
          minValue={20}
          maxValue={120}
          value={Number(setting.creditsSpeed ?? 60)}
          onChange={(val) => updateSetting('creditsSpeed', val)}
          aria-label="Scroll speed"
          classNames={{
            base: 'w-full cursor-pointer gap-3',
            track: 'slider-border-width h-1',
            filler: 'bg-red-500',
          }}
          renderThumb={(props) => (
            <div
              {...props}
              className="group p-1 top-1/2 bg-red-500 shadow-medium rounded-full cursor-grab data-[dragging=true]:cursor-grabbing"
            >
              <span className="bg-red-500 shadow-small rounded-full w-2 h-2 block" />
            </div>
          )}
        />
      </SettingBlock>
    </>}
  </>;
};

export default InteractionSetting;
